import { useMemo } from 'react';
import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { cn } from '@rocco/utils/cn';

import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    SectionHead,
} from '~/ui-rocco/pages/detail-shared';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';

/**
 * Five across, and five to a row on arrival.
 *
 * The tiles are rendered here rather than through `DetailsPageRelatedTileGrid`:
 * that grid pins a product tile to a fixed 437px on any `/project/` path, which
 * overrides whatever column count it is given and lands at four.
 */
export const GRID_CLASS =
    'grid grid-cols-2 max-md:gap-x-3.5 max-md:gap-y-8 md:grid-cols-5 md:gap-x-3 md:gap-y-[6.25rem]';

/**
 * The tiles' captions, repainted for a dark ground.
 *
 * The shared tiles are authored against the browse grids' near-white page, so
 * their title is #323232 and their supplier line #767676 — on this section's
 * #181818 the title all but disappears and the supplier is unreadable. Scoped
 * here rather than fixed in the tile because the tile is right for the ground
 * it was drawn for; it is this page that changed the ground.
 *
 * `rocco-tile-description-block` is the caption hook every tile type shares,
 * so this reaches the product tiles and the professional tiles alike. First
 * child is the title, last the name under it.
 */
export const DARK_TILE_CAPTIONS = cn(
    // Everything in the caption goes light first. The product tile wraps each
    // line in a link and the professional tile does not, so a rule written for
    // one shape misses the other; whitening the block and then dialling the
    // second line back covers both.
    '[&_.rocco-tile-description-block_span]:text-offwhite',
    '[&_.rocco-tile-description-block>*:nth-child(2)]:text-white/60',
    '[&_.rocco-tile-description-block>*:nth-child(2)_span]:text-white/60',
    // The tile darkens the name to the page ink on hover, which on this ground
    // would fade it out rather than pick it up.
    '[&_.rocco-tile-description-block>*:nth-child(2)]:hover:text-white',
    '[&_.rocco-tile-description-block>*:nth-child(2)_span]:hover:text-white',
);
const SHOWN = 5;

/**
 * The two closing inventories: what went into the project, and who built it.
 *
 * They use the ordinary product and professional tiles rather than the
 * editorial project tile the rest of this page's tail uses — these are things
 * to go and look at, and they read the same here as they do on a browse grid.
 */
const UsedSection = ({
    lead,
    subject,
    standfirst,
    section,
    tiles,
}: {
    lead: string;
    subject: string;
    standfirst: string;
    section: string;
    tiles: React.ComponentProps<
        typeof CategoryPageTileWithAdminActions
    >['tile'][];
}) => {
    const { desktop } = useAppDisplayModeContext();

    if (tiles.length === 0) return null;

    const shown = desktop ? tiles.slice(0, SHOWN) : tiles.slice(0, 4);

    return (
        <section className="max-md:px-6 md:px-[3rem]">
            <div className={CONTENT_CAP_PADDED}>
                {/* No eyebrow or number: these two close the page and the
                    heading already says what they are. */}
                <SectionHead
                    tone="dark"
                    lead={lead}
                    subject={subject}
                    standfirst={standfirst}
                />

                <div
                    className={cn(
                        GRID_CLASS,
                        DARK_TILE_CAPTIONS,
                        'max-md:mt-6 md:mt-[3.75rem]',
                    )}
                >
                    {shown.map((tile) => (
                        <CategoryPageTileWithAdminActions
                            key={`${section}-${tile.model.id}`}
                            tile={tile}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

/** Every product tagged into this project. */
export const ProjectProductsUsedSection = () => {
    const { featuredProducts } = useLoaderData<typeof loader>();

    // The loader's own array, not a defaulted copy: `?? []` builds a new one
    // every render, which the memo below would then never hold.
    const products = featuredProducts?.FeaturedProducts?.products;
    const tiles: TileViewModel<ProductTileViewModel>[] = useMemo(
        () =>
            (products ?? []).map((product) => ({
                model: product,
                aspectRatio: 'productTall' as const,
                section: 'ProjectUsedProducts',
            })),
        [products],
    );
    const withAdmin = useTilesAdminData(tiles);

    return (
        <UsedSection
            lead="Products used in"
            subject="this project"
            standfirst="Everything specified into this build, and who supplies it."
            section="ProjectUsedProducts"
            tiles={withAdmin}
        />
    );
};

/** Every practice credited on this project. */
export const ProjectProfessionalsUsedSection = () => {
    const { featuredProfessionals } = useLoaderData<typeof loader>();

    const professionals =
        featuredProfessionals?.FeaturedProfessionals?.professionals;
    const tiles: TileViewModel<ProfessionalTileViewModel>[] = useMemo(
        () =>
            (professionals ?? []).map((professional) => ({
                model: professional,
                aspectRatio: 'professional' as const,
                section: 'ProjectUsedProfessionals',
                // The reader already knows what these practices did — the
                // heading says it. What they do elsewhere and where they are
                // based is a browse grid's job, and five tiles of pills under
                // a project's closing inventory read as noise.
                hideServiceTags: true,
                hideLocation: true,
            })),
        [professionals],
    );
    const withAdmin = useTilesAdminData(tiles);

    return (
        <UsedSection
            lead="Professionals used in"
            subject="this project"
            standfirst="The practices and trades credited on this project."
            section="ProjectUsedProfessionals"
            tiles={withAdmin}
        />
    );
};
